import { CollectionHelper, CompareHelper, GUID } from '@barthauer/abc/core';
import { MapLoader } from './map.loader';
import { MapStyle } from './models/map.model';

declare let ol: any;
declare let turf: any;

class MapLabelItem {
  public anchor: [number, number];
  public boundingCoordinates: Array<[number, number]>;
  public boundingExtent: [number, number, number, number];
  public isApplied: boolean;

  constructor(
    public readonly feature: any,
    public readonly group: MapLabelGroup,
    public readonly mapStyle: MapStyle,
    public readonly style: any
  ) {}
}

class MapLabelGroup {
  public boundingPolygon: any;
  public boundingPolygonArea: number;
  public anchor: [number, number];
  public isReady: boolean;
  public readonly items: MapLabelItem[] = [];

  constructor(
    public readonly categoryName: string,
    public maxDistance: number,
    public minScale: number
  ) {}
}

export class MapLabelPlacer {
  static renderContext: CanvasRenderingContext2D;

  constructor(
    private center: number[],
    private rawResolution: number,
    private defaultGroupKey: string
  ) {
    if (!MapLabelPlacer.renderContext) {
      const canvas = document.createElement('canvas');
      MapLabelPlacer.renderContext = canvas.getContext('2d');
    }
  }

  static current: MapLabelPlacer;
  public static get(center: number[], rawResolution: number, defaultGroupKey: string) {
    const distance = Math.sqrt(
      Math.pow(this.current?.center[0] - center[0], 2) + Math.pow(this.current?.center[1] - center[1], 2)
    );
    const resolutiondiff = Math.abs(this.current?.rawResolution - rawResolution);
    if (distance !== undefined && distance < 1.0 && resolutiondiff !== undefined && resolutiondiff < 0.000001) {
      return this.current;
    } else {
      clearTimeout(this.current?.timerId);
      return (this.current = new MapLabelPlacer(center, rawResolution, defaultGroupKey));
    }
  }

  public get hasPendingStyles(): boolean {
    return !!Array.from(this.groups).find((e) => e[1].items.find((_) => !_.isApplied));
  }

  private timerId: NodeJS.Timeout;
  private features: Map<any, MapLabelItem[]> = new Map();
  private groups: Map<string, MapLabelGroup> = new Map();
  private geometryTree = new turf.geojsonRbush();
  private labelTrees: Map<string, any> = new Map();
  private geojson = new ol.format.GeoJSON();
  public append(
    feature: any,
    textStyles: { mapStyle: MapStyle; style: any }[],
    geometryStyles: { mapStyle: MapStyle; style: any }[],
    transformGeometry: (geom: any) => any,
    getResolution: (style: MapStyle) => number
  ): { mapStyle: MapStyle; style: any }[] | undefined {
    let items = this.features.get(feature);
    if (items && !items.find((_) => _.group?.isReady !== true)) {
      for (const item of items) {
        item.isApplied = true;
      }
      return items;
    }
    clearTimeout(this.timerId);
    if (items) {
      for (const item of items) {
        if (item.group) {
          item.group.isReady = false;
        }
        CollectionHelper.removeItem(item.group?.items, item);
      }
    }
    items = [];
    for (const e of textStyles) {
      let groupkey: string = undefined;
      if (e.mapStyle.text.declutterGroupColumnName) {
        groupkey = feature.get(e.mapStyle.text.declutterGroupColumnName);
      }
      if (!groupkey) {
        if (this.defaultGroupKey) {
          groupkey = this.defaultGroupKey + feature.getId();
        } else {
          groupkey = '';
        }
      }
      const fullgroupkey = groupkey + '$' + e.mapStyle.text.declutterCategoryName;
      const maxDistance = groupkey ? e.mapStyle.text.declutterMaxDistance : 0.0;
      const minScale = groupkey ? e.mapStyle.text.declutterMinScale : 1.0;
      let group = this.groups.get(fullgroupkey);
      if (!group) {
        const categoryName = groupkey ? e.mapStyle.text.declutterCategoryName : '';
        group = new MapLabelGroup(categoryName, maxDistance, minScale);
      }
      if (group.maxDistance < maxDistance) {
        group.maxDistance = maxDistance;
      }
      if (CompareHelper.isNullorEmpty(group.minScale) || group.minScale > minScale) {
        group.minScale = minScale;
      }
      this.groups.set(fullgroupkey, group);

      const item = new MapLabelItem(feature, group, e.mapStyle, e.style);
      items.push(item);
      group.items.push(item);
      group.isReady = false;
    }

    for (const e of geometryStyles) {
      const geom = transformGeometry(feature.getGeometry());
      const polygons = this.tryGetPolygonsForStyle(geom, e.style, getResolution(e.mapStyle));
      if (polygons) {
        for (const polygon of polygons) {
          this.geometryTree.insert({
            type: 'Feature',
            geometry: this.geojson.writeGeometryObject(polygon),
          });
        }
      }
    }

    this.features.set(feature, items);

    this.timerId = setTimeout(() => this.placeLabels(transformGeometry, getResolution), 100);
    return undefined;
  }

  private tryGetPolygonsForStyle(geom: any, style: any, resolution: number): any[] {
    let bufferDistance: number = undefined;
    const stroke = style?.getStroke();
    const image = style?.getImage();
    if (image || stroke) {
      bufferDistance = 0.0;
      if (image) {
        bufferDistance += image.getScale() / 2 / MapLoader.getSizeFactor(resolution);
        if (image?.imgSize?.length) {
          bufferDistance *= (image?.imgSize[0] + image?.imgSize[1]) / 2;
        }
      }
      if (stroke) {
        bufferDistance += stroke.getWidth() / 2 / MapLoader.getSizeFactor(resolution);
      }
    }
    if (bufferDistance !== undefined) {
      const type = geom?.getType();
      const buffers: Array<Array<[number, number]>> = [];
      switch (type) {
        case 'Point':
          const buffer = this.getBufferedExtentForCoordinate(geom.getCoordinates(), bufferDistance);
          if (buffer) {
            buffers.push(buffer);
          }
          break;
        case 'LineString':
          const coordinates: Array<[number, number]> = geom.getCoordinates();
          if (coordinates) {
            let lastBuffer: Array<[number, number]> = undefined;
            for (const coordinate of coordinates) {
              const buffer = this.getBufferedExtentForCoordinate(coordinate, bufferDistance);
              if (buffer) {
                if (lastBuffer) {
                  const temp: Array<[number, number]> = [];
                  temp.push(...lastBuffer);
                  temp.push(...buffer);
                  buffers.push(MapLabelPlacer.convexHull(temp));
                }
                lastBuffer = buffer;
              }
            }
          }
          break;
      }
      if (buffers?.length) {
        const polygons: any[] = [];
        for (const buffer of buffers) {
          buffer.push(buffer[0]);
          polygons.push(new ol.geom.Polygon([buffer]));
        }
        return polygons;
      }
    }
  }

  private getBufferedExtentForCoordinate(coordinate: [number, number], buffer: number): Array<[number, number]> {
    const extent = ol.extent.buffer(ol.extent.boundingExtent([coordinate]), buffer);
    const result: Array<[number, number]> = [];
    result.push(ol.extent.getBottomLeft(extent));
    result.push(ol.extent.getBottomRight(extent));
    result.push(ol.extent.getTopRight(extent));
    result.push(ol.extent.getTopLeft(extent));
    return result;
  }

  private placeLabels(transformGeometry: (geom: any) => any, getResolution: (style: MapStyle) => number): void {
    for (const e of this.features) {
      const feature = e[0];
      const items = e[1];
      const geom = transformGeometry(feature.getGeometry());
      const anchor = MapLabelPlacer.getLabelAnchor(geom);
      for (const item of items) {
        if (!item.boundingExtent) {
          if (anchor) {
            const style = item.style;
            const text = style.getText();
            const textValue: string = text.getText();
            if (textValue) {
              const scale = text.getScale();
              let scaleX = 1.0;
              let scaleY = 1.0;
              if (scale) {
                if (Array.isArray(scale)) {
                  scaleX = scale[0];
                  scaleY = scale[1];
                } else {
                  scaleX = scale;
                  scaleY = scale;
                }
              }
              MapLabelPlacer.renderContext.font = text.getFont();
              MapLabelPlacer.renderContext.textAlign = text.getTextAlign();
              MapLabelPlacer.renderContext.textBaseline = text.getTextBaseline();
              const metrics = MapLabelPlacer.renderContext.measureText(textValue);
              if (metrics?.width) {
                const factor = MapLoader.getTextSizeFactor(getResolution(item.mapStyle));
                let heightFromBaseline = scaleY / factor;
                const pixelHeightFromBaseline = metrics.fontBoundingBoxAscent - metrics.alphabeticBaseline;
                const baselineRatio = -metrics.alphabeticBaseline / pixelHeightFromBaseline;
                const belowAnchor = heightFromBaseline * baselineRatio;
                const aboveAnchor = heightFromBaseline - belowAnchor;

                const width =
                  heightFromBaseline *
                  ((metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight) / pixelHeightFromBaseline) *
                  (scaleX / scaleY) *
                  (4 / 3);
                const leftFromAnchor =
                  width *
                  (metrics.actualBoundingBoxLeft / (metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight));
                const rightFromAnchor =
                  width *
                  (metrics.actualBoundingBoxRight / (metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight));

                const bufferFactor = 1.0;
                const offsetLeft = -leftFromAnchor * bufferFactor;
                const offsetRight = rightFromAnchor * bufferFactor;
                const offsetTop = aboveAnchor * bufferFactor;
                const offsetBottom = -belowAnchor * bufferFactor;
                const rect: Array<[number, number]> = [
                  [offsetLeft, offsetBottom],
                  [offsetRight, offsetBottom],
                  [offsetRight, offsetTop],
                  [offsetLeft, offsetTop],
                ];

                let offset: [number, number] = [text.getOffsetX() ?? 0, text.getOffsetY() ?? 0];
                if (offset[0] || offset[1]) {
                  const sizeFactor = MapLoader.getSizeFactor(getResolution(item.mapStyle));
                  offset[0] /= sizeFactor;
                  offset[1] /= -sizeFactor;
                }

                const rotation: number = text.getRotation();
                if (rotation) {
                  for (let i = 0; i < rect.length; i++) {
                    rect[i] = ol.coordinate.rotate(rect[i], MapLoader.normalizeRotation(-rotation));
                  }
                  offset = ol.coordinate.rotate(offset, MapLoader.normalizeRotation(-rotation));
                }

                const coordinates: Array<[number, number]> = [];
                for (let i = 0; i < rect.length; i++) {
                  const c = rect[i];
                  coordinates.push([c[0] + anchor[0] + offset[0], c[1] + anchor[1] + offset[1]]);
                }
                coordinates.push(coordinates[0]);
                const polygon = new ol.geom.Polygon([coordinates]);

                item.boundingExtent = polygon.getExtent();
                item.anchor = anchor;
                item.boundingCoordinates = coordinates;
              }
            }
          }
        }
      }
    }

    let isReady = true;
    for (const group of this.groups.values()) {
      if (!group.isReady) {
        const points: Array<[number, number]> = [];
        for (const item of group.items) {
          if (item.boundingCoordinates) {
            points.push(...item.boundingCoordinates);
          }
        }
        const convexHull = MapLabelPlacer.convexHull(points);
        if (convexHull?.length) {
          convexHull.push(convexHull[0]);
          const polygon = new ol.geom.Polygon([convexHull]);
          group.boundingPolygon = polygon;
          group.anchor = polygon.getInteriorPoint().getCoordinates();
          group.boundingPolygonArea = polygon.getArea();
        } else {
          isReady = false;
        }
      }
    }
    const featuresToUpdate: any[] = [];
    if (isReady) {
      const orderedGroups = CollectionHelper.orderBy(
        Array.from(this.groups.values()),
        (g) => (g.maxDistance === 0.0 ? 0 : 1),
        (g) => (g.categoryName?.length ? -g.categoryName.length : 0),
        (g) => g.maxDistance,
        (g) => -g.items.length,
        (g) => g.boundingPolygonArea
      );
      let unplacedCount = 0;
      for (const g of orderedGroups) {
        if (g.isReady) {
          continue;
        }
        let declutterDistance = g.maxDistance;
        if (CompareHelper.isNullorEmpty(declutterDistance)) {
          declutterDistance = 2 * Math.round(Math.sqrt(orderedGroups[orderedGroups.length - 1].boundingPolygonArea));
        }
        const infos = MapLabelPlacer.getTransformInfo(declutterDistance, g.minScale);
        if (infos) {
          const key = g.categoryName ?? '';
          let labelTree = this.labelTrees.get(key);
          let candidate: {
            g: MapLabelGroup;
            feature: { type: string; geometry: any };
            movedPoly: any;
            transform: { scale: number; offset: [number, number] };
          } = undefined;
          for (const t of infos) {
            const scale = t.scale;
            const dx = t.offset[0];
            const dy = t.offset[1];
            const movedPoly = g.boundingPolygon.clone();
            movedPoly.scale(scale, scale, g.anchor);
            movedPoly.translate(dx, dy);
            const polygonData = this.geojson.writeGeometryObject(movedPoly);
            const feature = {
              type: 'Feature',
              geometry: polygonData,
            };
            let noCollision: boolean = true;
            for (let e of this.labelTrees) {
              if (!e[0].startsWith(key) && !key.startsWith(e[0])) {
                continue;
              }
              const neighbours = e[1].search(feature);
              const collision = neighbours.features?.find((n) => !turf.booleanDisjoint(feature, n));
              if (collision) {
                noCollision = false;
                break;
              }
            }
            if (noCollision) {
              let isBetterMatch = false;
              let isBestMatch = false;
              if (this.groups.size < 1000) {
                const geometries = this.geometryTree.search(feature);
                isBetterMatch = !geometries.features?.find((n) => !turf.booleanDisjoint(feature, n));
                isBestMatch = isBetterMatch;
              } else {
                isBestMatch = true;
              }
              if (!candidate || isBetterMatch) {
                candidate = { g: g, feature: feature, movedPoly, transform: t };
              }
              if (isBestMatch) {
                break;
              }
            }
          }
          if (candidate) {
            const g = candidate.g;
            const feature = candidate.feature;
            const movedPoly = candidate.movedPoly;
            if (!labelTree) {
              labelTree = new turf.geojsonRbush();
              this.labelTrees.set(key, labelTree);
            }
            labelTree.insert(feature);
            for (const i of g.items) {
              var text = i.style.getText();
              const transformScale = candidate.transform.scale;
              let dx = candidate.transform.offset[0];
              let dy = candidate.transform.offset[1];
              if (transformScale != 1.0) {
                let scale = text.getScale();
                if (Array.isArray(scale)) {
                  for (let i = 0; i < scale.length; i++) {
                    scale[i] *= transformScale;
                  }
                } else {
                  scale *= transformScale;
                }
                text.setScale(scale);

                let ax = i.anchor[0] - g.anchor[0];
                let ay = i.anchor[1] - g.anchor[1];
                dx += ax * (transformScale - 1.0);
                dy += ay * (transformScale - 1.0);
              }
              if (dx != 0.0 || dy != 0.0) {
                let d = [dx, dy];
                const rotation: number = text.getRotation();
                if (rotation) {
                  d = ol.coordinate.rotate(d, MapLoader.normalizeRotation(rotation));
                }
                const sizeFactor = MapLoader.getSizeFactor(getResolution(i.mapStyle));
                text.setOffsetX((text.getOffsetX() ?? 0) + d[0] * sizeFactor);
                text.setOffsetY((text.getOffsetY() ?? 0) - d[1] * sizeFactor);
              }
              featuresToUpdate.push(i.feature);
            }
            g.boundingPolygon = movedPoly;
            g.isReady = true;
          } else {
            for (const i of g.items) {
              if (unplacedCount++ < 10) {
                var text = i.style.getText();
                console.warn('Label not placed: ' + text.getText());
                text.setText('');
              }
            }
            g.isReady = true;
          }
        }
      }
    }
    CollectionHelper.distinct(featuresToUpdate).forEach((f) => f.changed());
  }

  private static convexHull(points: Array<[number, number]>): Array<[number, number]> {
    if (points.length <= 3) return points.slice(); // hull of 3 or fewer is itself

    // Sort points lexicographically (by x, then y)
    const sorted = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);

    const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
      (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

    const lower: Array<[number, number]> = [];
    for (const p of sorted) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }

    const upper: Array<[number, number]> = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
      const p = sorted[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }

    // Remove last point of each list (it's repeated at the beginning of the other)
    upper.pop();
    lower.pop();

    return lower.concat(upper);
  }

  private static getLabelAnchor(geom: any): [number, number] {
    const type = geom?.getType();
    switch (type) {
      case 'Point':
        return geom.getCoordinates();
      case 'LineString':
        return this.getLineMidpoint(geom);
      case 'Polygon':
        return this.getPolygonInteriorPoint(geom);
      case 'MultiPoint':
        return geom.getPoint(0)?.getCoordinates();
      case 'MultiLineString':
        return this.getLineMidpoint(geom.getLineString(0));
      case 'MultiPolygon':
        let largestPoly: any;
        let maxArea = 0;
        geom.getPolygons().forEach((p) => {
          const area = p.getArea();
          if (area > maxArea) {
            maxArea = area;
            largestPoly = p;
          }
        });
        return largestPoly ? this.getPolygonInteriorPoint(largestPoly) : null;
      case 'GeometryCollection':
        return this.getLabelAnchor(geom.getGeometries()[0]);
    }
    // Fallback for unsupported types
    const extent = geom?.getExtent();
    return ol.extent.getCenter(extent);
  }

  private static getLineMidpoint(line: any): [number, number] {
    const coords = line.getCoordinates();
    let totalLength = 0;
    const segmentLengths: number[] = [];

    for (let i = 0; i < coords.length - 1; i++) {
      const dx = coords[i + 1][0] - coords[i][0];
      const dy = coords[i + 1][1] - coords[i][1];
      const len = Math.hypot(dx, dy);
      segmentLengths.push(len);
      totalLength += len;
    }

    const target = totalLength / 2;
    let acc = 0;

    for (let i = 0; i < segmentLengths.length; i++) {
      const segLen = segmentLengths[i];
      if (acc + segLen >= target) {
        const ratio = (target - acc) / segLen;
        const x = coords[i][0] + (coords[i + 1][0] - coords[i][0]) * ratio;
        const y = coords[i][1] + (coords[i + 1][1] - coords[i][1]) * ratio;
        return [x, y];
      }
      acc += segLen;
    }
    return coords[Math.floor(coords.length / 2)];
  }

  private static getPolygonInteriorPoint(poly: any): [number, number] {
    // Try getInteriorPoint (accurate but heavy), fallback to centroid
    const interior = poly.getInteriorPoint?.();
    if (interior) return interior.getCoordinates();

    // Simple centroid fallback
    const coords = poly.getLinearRing(0).getCoordinates();
    let x = 0,
      y = 0;
    for (const c of coords) {
      x += c[0];
      y += c[1];
    }
    return [x / coords.length, y / coords.length];
  }

  static readonly offsetsCache: Map<string, Array<{ scale: number; offset: [number, number] }>> = new Map();

  private static getTransformInfo(
    maxDistance: number,
    minScale: number
  ): Array<{ scale: number; offset: [number, number] }> {
    if (maxDistance === undefined || maxDistance === null || maxDistance < 0) {
      maxDistance = 0.0;
    }
    const maxScale = 1.0;
    if (minScale === undefined || minScale === null || minScale < 0 || minScale > maxScale) {
      minScale = 1.0;
    }
    const key = Math.round(maxDistance * 100) + '|' + Math.round(minScale * 100);
    let result = this.offsetsCache.get(key);
    if (!result) {
      const ringCount = 4;
      const distancestep = maxDistance / ringCount;
      const downScaleCount = 2;
      const scalestep = (maxScale - minScale) / downScaleCount;
      result = [];
      for (let r = 0; r <= ringCount; r++) {
        const s = r * distancestep;
        for (let dx = -s; dx <= s; dx += distancestep) {
          for (let dy = s; dy >= -s; dy -= distancestep) {
            // only take points roughly on the perimeter of the square ring
            if (Math.abs(dx) == s || Math.abs(dy) == s) {
              for (let scale = maxScale; scale >= minScale; scale -= scalestep) {
                result.push({ scale: scale, offset: [dx, dy] });
                if (scalestep == 0.0) {
                  break;
                }
              }
            }
            if (distancestep == 0.0) {
              break;
            }
          }
          if (distancestep == 0.0) {
            break;
          }
        }
        if (distancestep == 0.0) {
          break;
        }
      }
      this.offsetsCache.set(key, result);
    }
    return result;
  }
}
